import { Component, Input, OnInit, ViewChild, signal } from '@angular/core';
import { UntypedFormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
} from '@angular/material/table';
import { collection, onSnapshot, orderBy, query, where, addDoc } from 'firebase/firestore';
import { dataStore, db, functions } from 'src/app/app.component';
import { LogType } from 'src/app/enums';
import { ucFirst } from 'src/app/globals';
import {
  GenericWarningDialogData,
  Vehicle,
  VehicleGroup,
  RidePreparationLog,
} from 'src/app/interfaces';
import {
  EditRidePreparationItemDialogComponent,
  Field,
} from '../../../ride-preparation/dialogs/edit-ride-preparation-item-dialog/edit-ride-preparation-item-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { WarningDialog } from 'src/app/global-dialogs/warning/warning.component';
import { httpsCallable } from 'firebase/functions';
import { MatCard } from '@angular/material/card';
import { MatFormField, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatIcon } from '@angular/material/icon';
import { MatInput } from '@angular/material/input';
import { MatIconButton } from '@angular/material/button';
import { NgStyle } from '@angular/common';
import { MatRipple } from '@angular/material/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-vehicle-groups',
  templateUrl: './vehicle-groups.component.html',
  styleUrl: './vehicle-groups.component.scss',
  imports: [
    MatCard,
    MatFormField,
    MatIcon,
    MatLabel,
    MatInput,
    FormsModule,
    ReactiveFormsModule,
    MatIconButton,
    MatSuffix,
    MatTable,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    NgStyle,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatRipple,
    MatProgressSpinner,
  ],
})
export class VehicleGroupsComponent implements OnInit {
  @Input() parentData: { processing: boolean };
  @ViewChild('table', { static: false }) table: MatTable<any>;

  vehicleGroups = signal<VehicleGroup[]>([]);
  filteredVehicleGroups = signal<VehicleGroup[]>([]);
  totalVehicleGroups = signal<number>(0);
  orderNumbersArray = signal<number[]>([]);
  vehicles = signal<Vehicle[]>([]);
  searchCtrl = new UntypedFormControl();
  displayedColumns = ['name', 'color', 'button'];
  organisationId = dataStore.selectedOrganisation.id;

  constructor(
    public dialog: MatDialog,
    public snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    onSnapshot(
      query(collection(db, `organisations/${this.organisationId}/vehicleGroups`), orderBy('name')),
      (snapshot) => {
        const vehicleGroups: VehicleGroup[] = [];
        const orderNumbers: number[] = [];
        this.totalVehicleGroups.set(snapshot.size);
        snapshot.forEach((doc) => {
          const data = doc.data() as VehicleGroup;
          const id = doc.id;
          data.name = ucFirst(data?.name ?? '');
          vehicleGroups.push({ id, ...data });
          orderNumbers.push(Number(data.orderNumber));
        });
        this.vehicleGroups.set(vehicleGroups);
        this.orderNumbersArray.set(orderNumbers);
        this.filteredVehicleGroups.set(
          this.filterVehicleGroups(this.searchCtrl.value, vehicleGroups),
        );
        if (this.table) {
          this.table.renderRows();
        }
      },
    );

    onSnapshot(
      query(
        collection(db, `organisations/${this.organisationId}/vehicles`),
        where('active', '==', true),
      ),
      (snapshot) => {
        const vehicles: Vehicle[] = [];
        snapshot.forEach((doc) => {
          const data = doc.data() as Vehicle;
          const id = doc.id;
          vehicles.push({ ...data, id });
        });
        this.vehicles.set(
          vehicles.sort((a, b) => a.calendarStyle.sortingNumber - b.calendarStyle.sortingNumber),
        );
      },
    );

    this.searchCtrl.valueChanges.subscribe((value) => {
      this.filteredVehicleGroups.set(this.filterVehicleGroups(value, this.vehicleGroups()));
      if (this.table) {
        this.table.renderRows();
      }
    });
  }

  clearSearch() {
    this.searchCtrl.setValue('');
    this.searchCtrl.updateValueAndValidity();
  }

  filterVehicleGroups(value: string, array: VehicleGroup[]): VehicleGroup[] {
    if (!value || value === '') {
      return array;
    }
    const filterValue = value.toLowerCase();
    return array.filter((vehicleGroup) => vehicleGroup.name.toLowerCase().includes(filterValue));
  }

  editItem(vehicleGroup?: VehicleGroup) {
    const fields: Field[] = [
      {
        key: 'name',
        type: 'string',
        label: 'Groepnaam',
        required: true,
      },
      {
        key: 'sortingNumber',
        type: 'order',
        label: 'Volgordenummer',
        required: true,
      },
      {
        key: 'color',
        type: 'color',
        label: 'Groep kleur',
        required: true,
      },
      {
        key: 'vehicles',
        type: 'checkboxGroup',
        label: 'Voertuigen',
        required: false,
        group: this.vehicles(),
      },
    ];
    const dialogRef = this.dialog.open(EditRidePreparationItemDialogComponent, {
      width: '400px',
      data: {
        item: vehicleGroup,
        itemLabel: 'voertuiggroep',
        fields: fields,
        optionalData: { orderNumbersArray: this.orderNumbersArray(), vehicles: this.vehicles() },
      },
    });
  }

  deleteItem(vehicleGroup: VehicleGroup) {
    const dialogRef = this.dialog.open(WarningDialog, {
      width: '500px',
      autoFocus: false,
      data: {
        dialogType: 'deleteVehicleGroups',
        optionalData: {
          vehicleGroupName: vehicleGroup.name,
          deleteMode: true,
        },
      } as GenericWarningDialogData,
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        try {
          const callable = httpsCallable(functions, 'httpDeleteVehicleGroups');
          await callable({
            vehicleGroupId: vehicleGroup.id,
            organisationId: this.organisationId,
          });
          await this.logDelete(vehicleGroup);
          this.snackBar.open('De voertuiggroep is verwijderd.', 'X', {
            duration: 5000,
          });
        } catch (error) {
          this.snackBar.open('Er ging iets mis bij het verwijderen van de voertuiggroep.', 'X', {
            duration: 5000,
          });
        }
      }
    });
  }

  async logDelete(item: VehicleGroup) {
    if (!item) {
      return;
    }

    const logObj: RidePreparationLog = {
      type: LogType.RidePreparationVehicleGroup,
      date: new Date(),
      action: 'delete',
      userEmail: dataStore.rootUser.email,
      userId: dataStore.rootUser.id,
      itemType: 'voertuiggroep',
      itemId: item.id,
      role:
        dataStore.rootUser.rights === 'admin'
          ? 'systemAdmin'
          : dataStore.orgUser?.roles?.join(', ') || 'onbekend',
      before: { ...item },
      after: null,
    };
    delete logObj.before.id;

    await addDoc(
      collection(
        db,
        `eventLogs/${dataStore.selectedOrganisation.id}/changedRidePreparationSettings`,
      ),
      logObj,
    );
  }
}
